import React, { useState, useEffect } from "react"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { CheckProductAvailability } from "aldoo-ra/Store/Availability"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import useProductEditorTexts from "../useProductEditorTexts"

const formatOptionValue = (val, options) => {
  if (val == null) return null

  // If val is a string, find the matching option
  if (typeof val === "string") {
    const matchingOption = options?.find((opt) => opt?.value === val)
    if (matchingOption) {
      return matchingOption
    }
    // Fallback: create option with same value and label
    return { value: val, label: String(val) }
  }

  // If val is already an option object with the right properties, return as is
  if (val && typeof val === "object" && "value" in val && ("label" in val || "name" in val)) {
    return val
  }

  // Fallback
  return { value: val, label: String(val) }
}

export default function AvailabilityResolverEditor({
  onChange,
  value,
  field,
  readOnly = false,
  parentObject,
  save,
}) {
  const t = useProductEditorTexts()
  const [isChecking, setIsChecking] = useState(false)
  const [availabilityStatus, setAvailabilityStatus] = useState(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [initialValue, setInitialValue] = useState(null)

  const resolveOptions = () => {
    let { options } = field
    if (!options) return []

    if (typeof options === "function") {
      return options()
    }

    if (Array.isArray(options)) {
      return options.map((option) => {
        if (typeof option === "string") {
          return {
            value: option,
            label: option,
          }
        }
        return option
      })
    }

    return options || []
  }

  const getInitialValue = () => {
    let defaultValue = field.default
    if (Array.isArray(defaultValue) && defaultValue.length > 0) {
      // Handle array default (take first item)
      defaultValue = formatOptionValue(defaultValue[0], resolveOptions())
    } else if (defaultValue !== undefined) {
      defaultValue = formatOptionValue(defaultValue, resolveOptions())
    }

    let initialValue = value !== undefined && value !== "" ? value : defaultValue
    
    if (initialValue !== undefined) {
      // Handle string values from server (when preFormat doesn't run)
      if (typeof initialValue === "string") {
        const options = resolveOptions()
        const matchingOption = options.find(opt => opt.value === initialValue)
        initialValue = matchingOption || formatOptionValue(initialValue, options)
      } else if (Array.isArray(initialValue) && initialValue.length > 0) {
        // Handle array values from server (take first item)
        const firstValue = initialValue[0]
        if (typeof firstValue === "string") {
          const options = resolveOptions()
          const matchingOption = options.find(opt => opt.value === firstValue)
          initialValue = matchingOption || formatOptionValue(firstValue, options)
        } else {
          // If it's an object but doesn't have the right properties, look up by value
          if (firstValue.value && !firstValue.label) {
            const options = resolveOptions()
            const matchingOption = options.find(opt => opt.value === firstValue.value)
            initialValue = matchingOption || formatOptionValue(firstValue, options)
          } else {
            initialValue = formatOptionValue(firstValue, resolveOptions())
          }
        }
      } else {
        initialValue = formatOptionValue(initialValue, resolveOptions())
      }
    }

    return initialValue || ""
  }

  const [localValue, setLocalValue] = useState(getInitialValue)

  // Track the initial value after mount; only a product with no way to check its availability yet
  // takes the default into its record — one that has one keeps it as loaded (writing it back in the
  // editor's own shape would read as a change nobody made)
  useEffect(() => {
    const initValue = getInitialValue()
    setInitialValue(initValue)
    const empty = value === undefined || value === null || value === "" || (Array.isArray(value) && !value.length)
    if (empty) onChange?.(initValue)
  }, []) // Empty dependency array ensures this only runs once after mount

  // Handle external value changes
  useEffect(() => {
    const currentValueStr = JSON.stringify(localValue?.value)
    const newValueStr = JSON.stringify(value?.value)

    if (currentValueStr !== newValueStr) {
      const newValue = getInitialValue()
      setLocalValue(newValue)
    }
  }, [value, field.default])

  const handleValueChange = (updatedValue) => {
    // a product always checks its availability one way: clicking the chosen
    // resolver again (which the dropdown reports as an empty selection) keeps it
    if (updatedValue == null || (Array.isArray(updatedValue) && updatedValue.length === 0)) return

    // Handle array from DropdownSelect (even in single selection mode, it might return an array)
    let actualValue = updatedValue
    if (Array.isArray(updatedValue) && updatedValue.length > 0) {
      actualValue = updatedValue[0]
    }

    // If the value has a nested 'value' property, extract it
    if (actualValue && actualValue.value && typeof actualValue.value === 'object') {
      actualValue = actualValue.value
    }

    const formattedValue = formatOptionValue(actualValue, resolveOptions())
    setLocalValue(formattedValue)
    onChange?.(formattedValue)

    // Check if value has changed from initial
    const hasChanged = JSON.stringify(initialValue?.value) !== JSON.stringify(formattedValue?.value)
    setHasUnsavedChanges(hasChanged)
  }

  const options = resolveOptions()

  const performAvailabilityCheck = async (savedProduct = null) => {
    // Use savedProduct if provided, otherwise call parentObject() to get the current data
    let product = savedProduct
    if (!product) {
      product = typeof parentObject === 'function' ? parentObject() : parentObject
    }

    // Extract the actual product data from API response structure
    // API returns { result: <product_data> } or just the product data
    let productData = product
    if (product?.result) {
      productData = product.result
    }

    // Try multiple possible locations for the product ID
    const productPid = productData?.pid || productData?._id

    if (!productPid) {
      setAvailabilityStatus({
        error: true,
        message: t`Please save the product first to check availability`
      })
      return
    }

    setIsChecking(true)
    setAvailabilityStatus(null)

    try {
      const response = await CheckProductAvailability(productPid)

      if (response.result) {
        setAvailabilityStatus({
          available: response.available,
          in_stock: response.in_stock,
          error: false
        })
      } else {
        setAvailabilityStatus({
          error: true,
          message: response.message || t`Failed to check availability`
        })
      }
    } catch (error) {
      setAvailabilityStatus({
        error: true,
        message: error.message || t`Error checking availability`
      })
    } finally {
      setIsChecking(false)
    }
  }

  const handleCheckAvailability = () => {
    // Check if there are unsaved changes
    if (hasUnsavedChanges) {
      showPromptDialog({
        title: t`Unsaved Changes`,
        message: t`You need to save the product before checking for availability.`,
        confirmText: t`Save Now`,
        cancelText: t`Cancel`,
        onConfirm: async () => {
          try {
            if (save && typeof save === 'function') {
              // Save the product
              showNotification({
                message: t`Saving product...`,
                className: "bg-admin_primary text-white",
              })

              const currentProduct = typeof parentObject === 'function' ? parentObject() : parentObject
              const saveResult = await save(currentProduct)

              // Only proceed if save was successful
              if (saveResult !== null && saveResult !== false) {
                // Reset unsaved changes flag after successful save
                setHasUnsavedChanges(false)
                setInitialValue(localValue)

                showNotification({
                  message: t`Product saved successfully`,
                  className: "bg-admin_primary text-white",
                })

                // Use currentProduct since it has the complete product data with pid
                // The saveResult only contains the _id, not the full product
                await performAvailabilityCheck(currentProduct)
              }
            } else {
              showNotification({
                message: t`Save function not available. Please save manually and try again.`,
                className: "bg-admin_error text-white",
              })
            }
          } catch (error) {
            // Save function threw an error (validation or save failed)
            console.error('Save failed:', error)
            showNotification({
              message: error.message || t`Failed to save product. Please check for validation errors.`,
              className: "bg-admin_error text-white",
            })
          }
        },
      })
    } else {
      performAvailabilityCheck()
    }
  }

  return (
    <div className="availability-resolver-editor">
      <div className="flex flex-col gap-3">
        <DropDownSelect
          variant="admin_highlighted"
          readOnly={readOnly}
          searchEnabled
          multiSelection={false}
          placeholder={t`Select availability resolver...`}
          options={options}
          enableResetButton={false}
          showCheckbox={false}
          showSelectionTick={true}
          onItemRender={(option) => {
            const label = option?.label || option?.name || option?.value
            const description = option?.description
            
            return (
              <div className="flex flex-col py-1">
                <div className="font-medium text-admin_text">
                  {label}
                </div>
                {description && (
                  <div className="text-admin_text_secondary text-sm mt-1">
                    {description}
                  </div>
                )}
              </div>
            )
          }}
          onValueItemRender={(selectedValue) => {
            if (!selectedValue) return t`Select availability resolver...`
            
            // Handle potential array wrapping
            let actualValue = selectedValue
            if (Array.isArray(selectedValue) && selectedValue.length > 0) {
              actualValue = selectedValue[0]
            }
            
            // Handle nested value structure
            if (actualValue && actualValue.value && typeof actualValue.value === 'object') {
              actualValue = actualValue.value
            }
            
            // Return the label (name) from the option object, not the value (ID)
            const displayName = actualValue?.label || actualValue?.name || actualValue?.value || String(actualValue)
            return displayName
          }}
          className="flex-grow text-admin_text bg-admin_paper"
          value={localValue}
          onChange={handleValueChange}
        />
        
        {/* Show description below dropdown for selected item */}
        {localValue && (() => {
          // Handle potential array wrapping
          let actualValue = localValue
          if (Array.isArray(localValue) && localValue.length > 0) {
            actualValue = localValue[0]
          }

          // Handle nested value structure
          if (actualValue && actualValue.value && typeof actualValue.value === 'object') {
            actualValue = actualValue.value
          }

          const description = actualValue?.description

          return description ? (
            <Typography 
              variant="admin_txt1_dim" 
              className="block px-1 mt-1"
            >
              {description}
            </Typography>
          ) : null
        })()}

        {/* Check Availability Button */}
        <div className="flex flex-row items-center gap-3 mt-2">
          <Button
            variant="admin_secondary"
            onClick={handleCheckAvailability}
            disabled={isChecking}
            className="w-fit"
          >
            {t`Check Availability`}
          </Button>

          {/* Availability Status Display */}
          {isChecking ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-admin_primary border-t-transparent"></div>

              <Typography variant="admin_txt1_dim">
                {t`Checking...`}
              </Typography>
            </div>
          ) : availabilityStatus && (
            <div className="flex items-center">
              {availabilityStatus.error ? (
                <Typography className="text-admin_error text-sm font-medium my-0">
                  {availabilityStatus.message || t`Error checking availability`}
                </Typography>
              ) : availabilityStatus.available ? (
                <Typography className="text-admin_success text-sm font-medium my-0">
                  ✓ {t`Product is available`}
                </Typography>
              ) : (
                <Typography className="text-admin_error text-sm font-medium my-0">
                  ✗ {t`Out of stock`}
                </Typography>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  )
}
